/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { ReactNode } from 'react';
import Head from 'next/head';
import RenderIf from '@components/RequestProject/RenderIf';
import VideoRequestBy from '@components/RequestProject/VideoRequestBy';
import { CampaignStyle } from '@interfaces';
import Logo from './components/Logo';
import Request from './styles/PageContainer';
import OpenGraph from './components/OpenGraph';
import TermsAndConditions from './styles/TermsAndConditions';
import RequestContent from './styles/RequestContent';
import RequestHead from './styles/RequestHead';
import RequestBody from './styles/RequestBody';
import RequestFooter from './styles/RequestFooter';

type Props = {
  background?: string;
  children?: ReactNode;
  domain: string;
  sendsparkBrandingEnabled?: boolean;
  whiteLabelAllowed?: boolean;
  style: CampaignStyle;
  title?: string;
  trackViralLink: (url?: string) => Promise<void>;
  workspaceName?: string;
};

const Layout = ({
  background,
  children,
  domain,
  sendsparkBrandingEnabled,
  whiteLabelAllowed,
  style,
  title = '',
  trackViralLink,
  workspaceName,
}: Props) => {
  const defaultFavicon = sendsparkBrandingEnabled ? '/assets/favicon/favicon.png' : '/assets/alternative-favicon/sendspark-white-label-favicon.png'
  const favicon = !whiteLabelAllowed
    ? '/assets/favicon/favicon.png'
    : style?.favicon?.url || defaultFavicon; 

  return (
    <>
      <OpenGraph
        domain={domain}
        sendsparkBrandingEnabled={sendsparkBrandingEnabled}
        workspaceName={workspaceName}
      />
      <Head>
        <title>{title}</title>
        <meta charSet="utf-8" />
        <meta name="viewport" content="initial-scale=1.0, width=device-width" />
        <link
          rel="preload"
          href="/fonts/Inter/Inter-Regular.otf"
          as="font"
          crossOrigin=""
        />
        <link
          rel="preload"
          href="/fonts/Inter/Inter-Regular.ttf"
          as="font"
          crossOrigin=""
        />
        <link
          rel="preload"
          href="/fonts/Inter/Inter-Bold.otf"
          as="font"
          crossOrigin=""
        />
        <link
          rel="preload"
          href="/fonts/Inter/Inter-Bold.ttf"
          as="font"
          crossOrigin=""
        />
        <link
          rel="preload"
          href="/fonts/Larsseit/Type Dynamic - Larsseit-Medium.otf"
          as="font"
          crossOrigin=""
        />
        <link
          rel="preload"
          href="/fonts/Larsseit/Type Dynamic - Larsseit-Medium.ttf"
          as="font"
          crossOrigin=""
        />
        <link
          rel="preload"
          href="/fonts/Larsseit/Type Dynamic - Larsseit Bold.otf"
          as="font"
          crossOrigin=""
        />
        <link
          rel="preload"
          href="/fonts/Larsseit/Type Dynamic - Larsseit Bold.ttf"
          as="font"
          crossOrigin=""
        />
        <link
          rel="preload"
          href="/fonts/Larsseit/Type Dynamic - Larsseit-Regular.otf"
          as="font"
          crossOrigin=""
        />
        <link
          rel="preload"
          href="/fonts/Larsseit/Type Dynamic - Larsseit-Regular.ttf"
          as="font"
          crossOrigin=""
        />
        <link
          rel="preload"
          href="/fonts/Larsseit/Type Dynamic - Larsseit-Light.otf"
          as="font"
          crossOrigin=""
        />
        <link
          rel="preload"
          href="/fonts/Larsseit/Type Dynamic - Larsseit-Light.ttf"
          as="font"
          crossOrigin=""
        />
        <link rel="shortcut icon" type="image/png" href={favicon} />
        <meta name="robots" content="noindex" />
      </Head>
      <Request background={background}>
        <RequestContent>
          <RequestHead logoSize={style?.logoSize}>
            <Logo src={style?.logo?.url} size={style?.logoSize} />
          </RequestHead>
          <RequestBody>{children}</RequestBody>
          <TermsAndConditions
            textColor={style?.heading2}
            responseButtonBackground={style?.responseButtonBackground}
          >
            <span>By submitting a video, you give usage rights to</span>{' '}
            <span>{workspaceName}</span>{' '}
            <span>
              based on existing Terms & Conditions
            </span>
          </TermsAndConditions>
          <RenderIf condition={!!sendsparkBrandingEnabled}>
            <RequestFooter>
              <VideoRequestBy
                textColor={style?.heading2}
                iconColor={style?.heading1}
                iconTextColor={style?.heading1}
                trackViralLink={trackViralLink}
              />
            </RequestFooter>
          </RenderIf>
        </RequestContent>
      </Request>
    </>
  );
};

export default Layout;
